import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function FuelCostCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Lifestyle", url: "https://calclab.app/lifestyle" },
      { name: "Fuel Cost Calculator", url: "https://calclab.app/calculator/fuel-cost" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Fuel Cost Calculator",
      "Calculate trip fuel expenses based on distance, MPG, and fuel price. Free online calculator.",
      "https://calclab.app/calculator/fuel-cost",
      lang
    )
  ]);
  const [distance, setDistance] = useState("");
  const [mpg, setMpg] = useState("");
  const [fuelPrice, setFuelPrice] = useState("");
  const [results, setResults] = useState<any>(null);

  const calculate = () => {
    const d = parseFloat(distance);
    const m = parseFloat(mpg);
    const p = parseFloat(fuelPrice);
    
    if (!d || !m || !p || d <= 0 || m <= 0 || p <= 0) return;
    
    const gallons = d / m;
    const cost = gallons * p;
    
    setResults({
      gallons: gallons.toFixed(2),
      cost: cost.toFixed(2),
      costPerMile: (cost / d).toFixed(3)
    });
  };

  return (
    <PageLayout calculatorName="Fuel Cost Calculator" calculatorPath="/calculator/fuel-cost">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Fuel Cost</CardTitle>
            <CardDescription>Estimate trip fuel expenses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Distance (miles)</Label>
              <Input type="number" value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="300" />
            </div>
            <div>
              <Label>Fuel Efficiency (MPG)</Label>
              <Input type="number" value={mpg} onChange={(e) => setMpg(e.target.value)} placeholder="30" />
            </div>
            <div>
              <Label>Fuel Price ($/gallon)</Label>
              <Input type="number" value={fuelPrice} onChange={(e) => setFuelPrice(e.target.value)} placeholder="3.50" step="0.01" />
            </div>
            <Button onClick={calculate} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Trip Cost</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-primary mb-2">${results.cost}</div>
                  <div className="text-lg text-muted-foreground">Total Fuel Cost</div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Fuel Needed:</span>
                    <span className="font-semibold">{results.gallons} gallons</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cost per Mile:</span>
                    <span className="font-semibold">${results.costPerMile}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <CalculatorSuperCalcCTA config={calculatorCTAs["fuel-cost"]} trigger={results.cost} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
